import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import { formatDate as formatDateUtil } from "../../../utils/DateUtils";
import { Tag } from "antd";
import DataTable from "../../../components/DataTable";
import Pagination from "../../components/Pagination";

function fmtDate(d) {
  if (!d) return "-";
  const s = formatDateUtil(d instanceof Date ? d.toISOString().slice(0, 10) : d);
  return s || "-";
}

function fmtDateTime(d) {
  if (!d) return "-";
  // รับ string format "YYYY-MM-DD HH:mm" จาก SQL โดยตรง — ไม่ผ่าน Date เพื่อหลีกเลี่ยง timezone shift
  const match = String(d).match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return "-";
  const datePart = formatDateUtil(match[1]) || match[1];
  return `${datePart} ${match[2]}:${match[3]}`;
}

/** ค่าที่แอปช่างบันทึกใน Step_Remark เมื่อ TE + เก็บคืน */
function getCollectCloseOutcomeBadge(text) {
  const t = text && typeof text === "string" ? text.trim() : "";
  if (t === "เก็บคืน") {
    return {
      label: "เก็บคืน",
      className: "bg-emerald-100 text-emerald-900 border border-emerald-300",
    };
  }
  if (t === "หาย") {
    return {
      label: "หาย",
      className: "bg-red-100 text-red-900 border border-red-300",
    };
  }
  if (t === "เก็บแล้วไม่ครบ") {
    return {
      label: "เก็บแล้วไม่ครบ",
      className: "bg-amber-100 text-amber-950 border border-amber-400",
    };
  }
  return null;
}

function getReturnOutcomeBadge(outcomeType) {
  const n = outcomeType != null ? Number(outcomeType) : NaN;
  if (!Number.isFinite(n)) return null;
  if (n === 1) {
    return {
      label: "คืนแล้วไม่ครบ",
      className: "bg-amber-100 text-amber-950 border border-amber-400",
    };
  }
  if (n === 2) {
    return {
      label: "คืนแล้ว",
      className: "bg-emerald-100 text-emerald-900 border border-emerald-300",
    };
  }
  if (n === 3) {
    return {
      label: "หาย",
      className: "bg-red-100 text-red-900 border border-red-300",
    };
  }
  return null;
}

/** แถวตัวอย่าง 3 แบบ — ใส่ URL ?collect_demo=1 เพื่อดูบนหน้ารายการ */
function buildCollectDemoRows() {
  const base = {
    job_id: null,
    placed_by: "—",
    team_name: "ตัวอย่าง",
    van_wh_name: "-",
    where_name: "จุดวาง (ตัวอย่าง)",
    where_full: "",
    round_no: 1,
    checkout_at: null,
    when_placed: null,
    status: 4,
    status_label: "คืนแล้ว",
    return_note: null,
    return_outcome_type: null,
    item_summary: "อุปกรณ์ชั่วคราว x1",
    warning: null,
    has_submitted: true,
    return_date: null,
    return_wh_name: null,
    return_by: null,
    return_tns_hint: "ตัวอย่างการแสดงผลเท่านั้น",
    remark: null,
    days_left: null,
    when_start: null,
    when_end: null,
  };
  return [
    {
      ...base,
      wh_doc_temp_id: "demo-collect-full",
      contract_no: "(ตัวอย่าง) เก็บคืนครบ",
      return_outcome_type: 2,
      collect_close_remark: "เก็บคืน",
    },
    {
      ...base,
      wh_doc_temp_id: "demo-collect-lost",
      contract_no: "(ตัวอย่าง) ของหาย",
      return_outcome_type: 3,
      collect_close_remark: "หาย",
    },
    {
      ...base,
      wh_doc_temp_id: "demo-collect-partial",
      contract_no: "(ตัวอย่าง) คืนไม่ครบ",
      return_outcome_type: 1,
      collect_close_remark: "เก็บแล้วไม่ครบ",
    },
  ];
}

/** จุดวาง: ตัดคำว่า "บ้าน" ที่ขึ้นต้น / วลี "บ้านลูกค้า" — ไม่แตะคำอย่าง "หมู่บ้าน" กลางประโยค */
function formatPlacementText(s) {
  if (s == null) return "";
  let t = String(s).trim();
  if (!t) return "";
  t = t.replace(/บ้านลูกค้า/g, "ลูกค้า");
  t = t.replace(/^บ้าน\s*/u, "").trim();
  return t;
}

function getPlacementPrimary(row) {
  const name = formatPlacementText(row?.where_name);
  if (name) return name;
  return formatPlacementText(row?.where_full);
}

function getPlacementSecondary(row) {
  const name = formatPlacementText(row?.where_name);
  const full = formatPlacementText(row?.where_full);
  if (full && name && full !== name) return full;
  if (full && !name) return full;
  return "";
}

function extractSelectedItemQty(itemSummary, selectedItemName) {
  if (!itemSummary || !selectedItemName) return 0;
  const parts = String(itemSummary).split(/[,\n]/);
  let total = 0;
  parts.forEach((part) => {
    const trimmed = part.trim();
    if (!trimmed) return;
    const match = trimmed.match(/^(.+?)\s+x([\d.]+)(?:\s+(.+))?$/);
    const name = match ? match[1].trim() : trimmed;
    if (name !== selectedItemName) return;
    const qty = match ? Number.parseFloat(match[2]) : 1;
    if (Number.isFinite(qty)) total += qty;
  });
  return total;
}

function getItemSummaryTotalQty(itemSummary) {
  if (!itemSummary) return 0;
  const parts = String(itemSummary).split(/[,\n]/);
  let total = 0;
  parts.forEach((part) => {
    const trimmed = part.trim();
    if (!trimmed) return;
    const match = trimmed.match(/^(.+?)\s+x([\d.]+)(?:\s+(.+))?$/);
    const qty = match ? Number.parseFloat(match[2]) : 1;
    if (Number.isFinite(qty)) total += qty;
  });
  return total;
}

function getCustomerName(row) {
  return (
    row?.customer_name ||
    row?.Customer_Name ||
    row?.customer ||
    row?.cus_name ||
    row?.Debtor_Name ||
    row?.debtor_name ||
    row?.contract_customer_name ||
    row?.contract_customer ||
    "-"
  );
}

function isCollectedRow(row) {
  if (!row || typeof row !== "object") return false;
  if (Number(row.status) === 4) return true;
  if (row.return_outcome_type != null || row.Return_Outcome_Type != null) return true;
  if (row.return_date || row.Return_Date) return true;
  const collectRemark = String(row.collect_close_remark || row.Collect_Close_Remark || "").trim();
  if (collectRemark === "เก็บคืน" || collectRemark === "เก็บแล้วไม่ครบ" || collectRemark === "หาย") {
    return true;
  }
  const returnNote = String(row.return_note || row.Return_Qty_Note || "").trim();
  if (returnNote.length > 0) return true;
  const statusLabel = String(row.status_label || row.Status_Label || "").trim();
  if (statusLabel.includes("คืนแล้ว") || statusLabel.includes("เก็บคืน")) return true;
  return false;
}

function normalizeItemKeyFromSummary(itemSummary) {
  const raw = String(itemSummary || "").trim();
  if (!raw) return "-";
  return raw.replace(/\s+x[\d.]+(?:\s+\S+)?$/u, "").trim() || raw;
}

/** job_id = Contract_Id (Doc_Id) จาก backend — ใช้กับ /contract/view/:id */
function ContractNoCell({ row, backLink, backLabel }) {
  const label = row.contract_no || "-";
  if (row.job_id && row.contract_no) {
    return (
      <Link
        to={`/contract/view/${row.job_id}`}
        state={{
          backLink,
          backLabel: backLabel || "รายการอุปกรณ์ชั่วคราว",
          fromTempEquipment: true,
          tempEquipmentBackLink: backLink,
          tempEquipmentBackLabel: backLabel || "รายการอุปกรณ์ชั่วคราว",
        }}
        className="text-blue-600 hover:text-blue-800 hover:underline"
      >
        {row.contract_no}
      </Link>
    );
  }
  return <span>{label}</span>;
}

export default function TempEquipmentListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryState = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => queryState.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(() => queryState.get("sf") || "");
  const [currentPage, setCurrentPage] = useState(() => {
    const p = Number.parseInt(queryState.get("p") || "1", 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const s = Number.parseInt(queryState.get("ps") || "100", 10);
    return Number.isFinite(s) && s > 0 ? s : 100;
  });
  const [sortConfig, setSortConfig] = useState(() => ({
    key: queryState.get("sk") || "when_end",
    direction: queryState.get("sd") || "desc",
  }));
  const [activeTab, setActiveTab] = useState(() => queryState.get("tab") || "docs");
  const [searchInput, setSearchInput] = useState(() => queryState.get("q") || "");
  const [selectedItemName, setSelectedItemName] = useState(() => queryState.get("item") || null);
  const [focusedDocKey, setFocusedDocKey] = useState(() => queryState.get("focusDoc") || null);
  const [focusedItemContractKey, setFocusedItemContractKey] = useState(() => queryState.get("focusItem") || null);
  const currentPath = `${location.pathname}${location.search || ""}`;

  const backLinkWithState = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    if (search) qs.set("q", search); else qs.delete("q");
    if (statusFilter) qs.set("sf", statusFilter); else qs.delete("sf");
    if (activeTab) qs.set("tab", activeTab); else qs.delete("tab");
    if (selectedItemName) qs.set("item", selectedItemName); else qs.delete("item");
    if (focusedDocKey) qs.set("focusDoc", focusedDocKey); else qs.delete("focusDoc");
    if (focusedItemContractKey) qs.set("focusItem", focusedItemContractKey); else qs.delete("focusItem");
    qs.set("p", String(currentPage || 1));
    qs.set("ps", String(itemsPerPage || 100));
    qs.set("sk", sortConfig?.key || "when_end");
    qs.set("sd", sortConfig?.direction || "desc");
    const q = qs.toString();
    return `${location.pathname}${q ? `?${q}` : ""}`;
  }, [
    location.pathname,
    location.search,
    search,
    statusFilter,
    activeTab,
    selectedItemName,
    focusedDocKey,
    focusedItemContractKey,
    currentPage,
    itemsPerPage,
    sortConfig,
  ]);

  const buildBackLinkForDocRow = (groupKey) => {
    const qs = new URLSearchParams(backLinkWithState.split("?")[1] || "");
    if (groupKey) qs.set("focusDoc", String(groupKey));
    qs.delete("focusItem");
    qs.set("tab", "docs");
    const q = qs.toString();
    return `${location.pathname}${q ? `?${q}` : ""}`;
  };

  const buildBackLinkForItemRow = (row) => {
    const focusKey = `${String(row.contract_no || "-")}__${String(getCustomerName(row) || "-")}`;
    const qs = new URLSearchParams(backLinkWithState.split("?")[1] || "");
    qs.set("tab", "items");
    if (selectedItemName) qs.set("item", selectedItemName);
    qs.set("focusItem", focusKey);
    qs.delete("focusDoc");
    const q = qs.toString();
    return `${location.pathname}${q ? `?${q}` : ""}`;
  };

  // sync state -> query string เพื่อให้กลับมาแล้วยัง active เหมือนเดิม
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (search) qs.set("q", search); else qs.delete("q");
    if (statusFilter) qs.set("sf", statusFilter); else qs.delete("sf");
    if (activeTab) qs.set("tab", activeTab); else qs.delete("tab");
    if (selectedItemName) qs.set("item", selectedItemName); else qs.delete("item");
    if (focusedDocKey) qs.set("focusDoc", focusedDocKey); else qs.delete("focusDoc");
    if (focusedItemContractKey) qs.set("focusItem", focusedItemContractKey); else qs.delete("focusItem");
    qs.set("p", String(currentPage || 1));
    qs.set("ps", String(itemsPerPage || 100));
    qs.set("sk", sortConfig?.key || "when_end");
    qs.set("sd", sortConfig?.direction || "desc");
    const nextSearch = qs.toString();
    const currentSearch = (location.search || "").replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      navigate(`${location.pathname}?${nextSearch}`, { replace: true });
    }
  }, [
    navigate,
    location.pathname,
    location.search,
    search,
    statusFilter,
    activeTab,
    selectedItemName,
    focusedDocKey,
    focusedItemContractKey,
    currentPage,
    itemsPerPage,
    sortConfig,
  ]);

  const fetchList = async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams();
      if (statusFilter) q.set("status", statusFilter);
      const query = q.toString();
      const suffix = query ? `?${query}` : "";
      // หน้า /temp-equipment เป็นมุมมองแอดมิน: ต้องลอง endpoint admin ก่อน
      const endpointCandidates = ["/app/admin/temp-equipment", "/app/temp-equipment"];

      let loaded = false;
      for (const endpoint of endpointCandidates) {
        try {
          const res = await api.get(`${endpoint}${suffix}`);
          if (res.data?.status === "OK") {
            const apiRows = res.data.result || [];
const collectDemo =
              typeof window !== "undefined" &&
              new URLSearchParams(window.location.search).get("collect_demo") === "1";
            setRows(collectDemo ? [...buildCollectDemoRows(), ...apiRows] : apiRows);
            loaded = true;
            break;
          }
        } catch (err) {
          // fallback ไป endpoint ถัดไป
        }
      }

      if (!loaded) {
        setRows([]);
      }
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: e.response?.data?.error || e.message,
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const q = qs.get("q") || "";
    const sf = qs.get("sf") || "";
    const tab = qs.get("tab") || "docs";
    const item = qs.get("item") || null;
    const focusDoc = qs.get("focusDoc") || null;
    const focusItem = qs.get("focusItem") || null;
    const p = Number.parseInt(qs.get("p") || "1", 10);
    const ps = Number.parseInt(qs.get("ps") || "100", 10);
    const sk = qs.get("sk") || "when_end";
    const sd = qs.get("sd") || "desc";

    setSearch(q);
    setSearchInput(q);
    setStatusFilter(sf);
    setActiveTab(tab);
    setSelectedItemName(item);
    setFocusedDocKey(focusDoc);
    setFocusedItemContractKey(focusItem);
    setCurrentPage(Number.isFinite(p) && p > 0 ? p : 1);
    setItemsPerPage(Number.isFinite(ps) && ps > 0 ? ps : 100);
    setSortConfig({ key: sk, direction: sd });
  }, [location.search]);

  const activeRows = useMemo(() => {
    // แสดงเฉพาะรายการที่ "วางจริงแล้ว" (มีวันวาง/check-out)
    // ถ้ายัง assign อย่างเดียว แต่ยังไม่จ่ายของ/ยังไม่ check-out จะยังไม่ขึ้นใน /temp-equipment
    const placedRows = rows.filter((r) => {
      const hasPlacedDate = Boolean(r?.when_placed || r?.checkout_at);
      const collected = isCollectedRow(r);
      return hasPlacedDate || collected;
    });

    const grouped = new Map();
    placedRows.forEach((r) => {
      const contractNo = String(r.contract_no || r.Contract_No || "-");
      const itemKey = normalizeItemKeyFromSummary(r.item_summary || r.Item_Summary || "");
      const key = `${contractNo}__${itemKey}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(r);
    });

    const filteredRows = [];
    grouped.forEach((groupRows) => {
      // แสดงเฉพาะแถวที่ยังไม่เก็บคืน
      const uncollectedRows = groupRows.filter((r) => !isCollectedRow(r));
      if (!uncollectedRows.length) return;

      // หลาย round ต่อ contract+item:
      // ถ้ายังไม่เก็บคืน ให้ยึด "วันวางที่เก่าสุด" (วางครั้งแรกที่ยังค้างอยู่)
      const oldestPlaced = uncollectedRows.reduce((picked, r) => {
        const pickedTs = picked?.when_placed ? new Date(picked.when_placed).getTime() : NaN;
        const currentTs = r?.when_placed ? new Date(r.when_placed).getTime() : NaN;

        if (!Number.isFinite(pickedTs) && Number.isFinite(currentTs)) return r;
        if (!Number.isFinite(currentTs)) return picked;
        if (currentTs < pickedTs) return r;
        if (currentTs > pickedTs) return picked;

        // ถ้าวันวางเท่ากัน/ไม่มีวันวาง ใช้ id/rnd ต่ำกว่าเป็นตัวแทน (เก่ากว่า)
        const pickedId = Number(picked?.wh_doc_temp_id ?? picked?.round_no ?? Number.MAX_SAFE_INTEGER);
        const currentId = Number(r?.wh_doc_temp_id ?? r?.round_no ?? Number.MAX_SAFE_INTEGER);
        return currentId < pickedId ? r : picked;
      }, uncollectedRows[0]);

      filteredRows.push(oldestPlaced);
    });
    return filteredRows;
  }, [rows]);

  const filtered = useMemo(() => {
    const key = search.toLowerCase().trim();
    if (!key) return activeRows;
    return activeRows.filter(
      (r) =>
        String(r.contract_no || "")
          .toLowerCase()
          .includes(key) ||
        String(r.where_name || "")
          .toLowerCase()
          .includes(key) ||
        String(r.where_full || "")
          .toLowerCase()
          .includes(key) ||
        String(r.item_summary || "")
          .toLowerCase()
          .includes(key) ||
        String(r.placed_by || "")
          .toLowerCase()
          .includes(key) ||
        String(r.team_name || "")
          .toLowerCase()
          .includes(key) ||
        String(r.van_wh_name || "")
          .toLowerCase()
          .includes(key) ||
        String(r.collect_close_remark || "")
          .toLowerCase()
          .includes(key)
    );
  }, [activeRows, search]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (!sortConfig.key) return list;
    const { key, direction } = sortConfig;
    const mul = direction === "asc" ? 1 : -1;

    return list.sort((a, b) => {
      if (key === "when_end" && direction === "upcoming") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const aDate = a.when_end ? new Date(a.when_end) : null;
        const bDate = b.when_end ? new Date(b.when_end) : null;
        const aActive = aDate && aDate >= today && Number(a.status) === 1;
        const bActive = bDate && bDate >= today && Number(b.status) === 1;
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        if (aActive && bActive) return aDate - bDate; // ใกล้ครบกำหนดก่อน
        return bDate - aDate; // expired: เพิ่งหมดก่อน
      }
      if (key === "when_end" && direction === "overdue") {
        const aOverdue = Number(a.status) === 2;
        const bOverdue = Number(b.status) === 2;
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;
        const aD = a.when_end ? new Date(a.when_end).getTime() : 0;
        const bD = b.when_end ? new Date(b.when_end).getTime() : 0;
        return aD - bD; // วันที่น้อยสุดขึ้นก่อน
      }
      if (key === "when_placed" || key === "when_end") {
        const aD = a[key] ? new Date(a[key]).getTime() : 0;
        const bD = b[key] ? new Date(b[key]).getTime() : 0;
        return (aD - bD) * mul;
      }
      if (key === "status") {
        const aN = Number(a.status) || 0;
        const bN = Number(b.status) || 0;
        return (aN - bN) * mul;
      }
      let aV = a[key];
      let bV = b[key];
      if (aV == null) aV = "";
      if (bV == null) bV = "";
      aV = String(aV);
      bV = String(bV);
      return aV.localeCompare(bV, "th", { numeric: true }) * mul;
    });
  }, [filtered, sortConfig]);

  const totalItems = sorted.length;
  const pageSize = Number.isFinite(Number(itemsPerPage)) && Number(itemsPerPage) > 0 ? Number(itemsPerPage) : 100;
  const page = Number.isFinite(Number(currentPage)) && Number(currentPage) > 0 ? Number(currentPage) : 1;

  // Group rows by contract_no
  const grouped = useMemo(() => {
    const map = new Map();
    sorted.forEach((r) => {
      const key = r.contract_no || r.wh_doc_temp_id || String(Math.random());
      if (!map.has(key)) map.set(key, { key, items: [] });
      map.get(key).items.push(r);
    });
    return [...map.values()];
  }, [sorted]);

  const totalGroups = grouped.length;
  const totalPages = Math.max(1, Math.ceil(totalGroups / pageSize));
  const safeCurrentPage = Math.min(page, totalPages);
  const groupFrom = (safeCurrentPage - 1) * pageSize;
  const paginatedGroups = grouped.slice(groupFrom, groupFrom + pageSize);

  // รายการอุปกรณ์เฉพาะ: group by item name จาก item_summary
  const itemStats = useMemo(() => {
    const map = {};
    activeRows.forEach((r) => {
      if (!r.item_summary) return;
      r.item_summary.split(/[,\n]/).forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;
        // รองรับ "ชื่อสินค้า x2", "ชื่อสินค้า x2.5", "ชื่อสินค้า x2 ตัว"
        const match = trimmed.match(/^(.+?)\s+x([\d.]+)(?:\s+(.+))?$/);
        const name = match ? match[1].trim() : trimmed;
        const qty = match ? parseFloat(match[2]) : 1;
        const unit = match ? (match[3] || "").trim() : "";
        if (!map[name]) map[name] = { name, count: 0, unit };
        map[name].count += qty;
      });
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name, "th"));
  }, [activeRows]);

  const itemFilteredRows = useMemo(() => {
    if (!selectedItemName) return [];
    const filteredByItem = activeRows.filter((r) => extractSelectedItemQty(r.item_summary, selectedItemName) > 0);
    const map = new Map();
    filteredByItem.forEach((r) => {
      const contractNo = String(r.contract_no || "-");
      const customerName = String(getCustomerName(r));
      const groupKey = `${contractNo}__${customerName}`;
      const qty = extractSelectedItemQty(r.item_summary, selectedItemName);
      const existing = map.get(groupKey);
      if (!existing) {
        map.set(groupKey, {
          ...r,
          customer_name_display: customerName,
          item_qty_in_contract: qty,
        });
      } else {
        existing.item_qty_in_contract += qty;
        // คงข้อมูลเดิมให้เห็นในตารางขวา โดยรวมค่าสำคัญระดับสัญญา
        const currentPlaced = existing.checkout_at || existing.when_placed;
        const nextPlaced = r.checkout_at || r.when_placed;
        if (!currentPlaced || (nextPlaced && nextPlaced < currentPlaced)) {
          existing.checkout_at = r.checkout_at;
          existing.when_placed = r.when_placed;
        }
        if (!existing.when_end || (r.when_end && r.when_end > existing.when_end)) {
          existing.when_end = r.when_end;
        }
        if (!existing.where_name && r.where_name) existing.where_name = r.where_name;
        if (!existing.where_full && r.where_full) existing.where_full = r.where_full;
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const c = String(a.contract_no || "").localeCompare(String(b.contract_no || ""), "th", { numeric: true });
      if (c !== 0) return c;
      return String(a.customer_name_display || "").localeCompare(String(b.customer_name_display || ""), "th", { numeric: true });
    });
  }, [activeRows, selectedItemName]);

  const selectedItemUnit = useMemo(() => {
    if (!selectedItemName) return "";
    const found = itemStats.find((x) => x.name === selectedItemName);
    return (found?.unit || "").trim();
  }, [itemStats, selectedItemName]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, activeRows.length, activeTab]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon icon="mdi:unfold-more-horizontal" className="w-4 h-4 inline ml-1" />;
    }
    return sortConfig.direction === "asc" ? (
      <Icon icon="mdi:chevron-up" className="w-4 h-4 inline ml-1" />
    ) : (
      <Icon icon="mdi:chevron-down" className="w-4 h-4 inline ml-1" />
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main
        className="flex-1 bg-gray-50 transition-all duration-300"
        style={{ marginLeft: "var(--sidebar-width)", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <Topbar
          className="pl-0"
          leftOffset="calc(var(--sidebar-width) - 8px)"
          breadcrumbs={[
            { label: "งานช่าง", link: "/job-management" },
            { label: "รายการอุปกรณ์ชั่วคราว" },
          ]}
        />

        <div className="pl-2 pr-8 pb-4" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", paddingTop: 72 }}>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]" />
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          ) : (
            <div className="card shadow-sm" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <style>{`
                .temp-equip-page * { font-size: 14px !important; }
                .temp-equip-page table.table {
                  border-collapse: separate !important;
                  border-spacing: 1px !important;
                  width: 100%;
                  min-width: 1050px;
                }
                .temp-equip-page th,
                .temp-equip-page td {
                  padding: 8px 10px !important;
                  background: white !important;
                  text-align: center;
                  outline: 1px solid #d0d0d0;
                  outline-offset: -1px;
                  white-space: nowrap;
                }
                .temp-equip-page thead th {
                  background-color: #E13F57 !important;
                  color: white !important;
                  font-weight: 600;
                  text-align: center;
                  z-index: 10;
                }
                .temp-equip-page thead th.sortable {
                  cursor: pointer;
                  user-select: none;
                  padding-left: 25px !important;
                  padding-right: 25px !important;
                }
                .temp-equip-page thead th.sortable:hover {
                  background-color: #c9354d !important;
                }
                .temp-equip-page th.col-seq,
                .temp-equip-page td.col-seq {
                  width: 50px;
                }
                .temp-equip-page th.col-contract,
                .temp-equip-page td.col-contract {
                  width: 140px;
                }
                .temp-equip-page thead th.col-contract {
                  text-align: center !important;
                }
                .temp-equip-page th.col-qty-sum,
                .temp-equip-page td.col-qty-sum {
                  width: 70px;
                }
                .temp-equip-page th.col-place,
                .temp-equip-page td.col-place {
                  width: 300px;
                  min-width: 180px;
                }
                .temp-equip-page th.col-date-placed,
                .temp-equip-page td.col-date-placed {
                  width: 150px;
                }
                .temp-equip-page th.col-date-end,
                .temp-equip-page td.col-date-end {
                  width: 110px;
                }
                .temp-equip-page th.col-status,
                .temp-equip-page td.col-status {
                  width: 105px;
                }
                .temp-equip-page th.col-team,
                .temp-equip-page td.col-team {
                  width: 120px;
                }
                .temp-equip-page td.cell-multiline {
                  white-space: normal !important;
                  text-align: left !important;
                  vertical-align: top;
                  overflow: visible;
                }
                .temp-equip-page td.sticky-action { text-align: center !important; vertical-align: middle !important; }
                .temp-equip-page td.sticky-action,
                .temp-equip-page th.sticky-action {
                  position: sticky; right: 0;
                  width: 60px;
                  background: white !important;
                  z-index: 20; isolation: isolate;
                  box-shadow: -3px 0 5px rgba(0,0,0,0.12);
                  padding: 8px 10px !important;
                }
                .temp-equip-page tbody tr:hover td.sticky-action { background: #f5f7ff !important; }
                .temp-equip-page thead th.sticky-action { background-color: #E13F57 !important; z-index: 21 !important; }
                .temp-equip-page tr.active-focus-row td {
                  background-color: #fce8eb !important;
                }
                .temp-equip-page .ant-table-tbody > tr.active-focus-row > td {
                  background-color: #fce8eb !important;
                }
                tr.active-focus-row td {
                  background-color: #fce8eb !important;
                }
                .ant-table-tbody > tr.active-focus-row > td {
                  background-color: #fce8eb !important;
                }
              `}</style>

              <div className="tab-content" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {/* Search bar + toggle */}
                <div className="temp-equip-page d-flex align-items-center px-4 py-2 border-bottom gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab("docs")}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === "docs"
                          ? "bg-[#E13F57] text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      <Icon icon="mdi:file-document-multiple" className="w-4 h-4 inline mr-1" />
                      รายการเอกสาร
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("items")}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === "items"
                          ? "bg-[#E13F57] text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      <Icon icon="mdi:package-variant" className="w-4 h-4 inline mr-1" />
                      รายการอุปกรณ์
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="ค้นหา... (Enter)"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { setSearch(searchInput); setCurrentPage(1); } }}
                      className="h-[36px] w-[250px] border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm"
                    />
                    {searchInput !== "" && (
                      <button
                        type="button"
                        onClick={() => { setSearchInput(""); setSearch(""); setCurrentPage(1); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <Icon icon="mdi:close" className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <label className="text-sm fw-medium mb-0" style={{ width: "50px" }}>สถานะ:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                    style={{ width: "fit-content" }}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="1">อยู่ในสัญญา</option>
                    <option value="2">ครบกำหนด</option>
                  </select>
                  <label className="text-sm fw-medium mb-0" style={{ width: "60px" }}>เรียงตาม:</label>
                  <select
                    value={sortConfig.key === "when_end" ? sortConfig.direction : ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) setSortConfig({ key: "when_end", direction: "desc" });
                      else setSortConfig({ key: "when_end", direction: val });
                    }}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                    style={{ width: "fit-content" }}
                  >
                    <option value="">ค่าเริ่มต้น</option>
                    <option value="upcoming">ใกล้ครบกำหนด</option>
                    <option value="overdue">วันที่ครบกำหนด</option>
                  </select>
                  {activeTab === "items" && selectedItemName && (
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: "#E13F57", fontWeight: 600, fontSize: 14 }}>•</span>
                      <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{selectedItemName}</span>
                    </div>
                  )}
                </div>

                {/* Tab: รายการเอกสาร */}
                {activeTab === "docs" && (
                  <div className="tab-pane fade show active" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
                    <div className="temp-equip-page" style={{ flex: 1, overflowX: "auto", overflowY: "auto", minHeight: 0 }}>
                      <table className="table mb-0">
                        <thead>
                          <tr>
                            <th className="col-seq">ลำดับ</th>
                            <th className="sortable col-contract" onClick={() => handleSort("contract_no")}>เลขที่สัญญา {getSortIcon("contract_no")}</th>
                            <th className="col-qty-sum">จำนวนรวม</th>
                            <th className="sortable col-place" onClick={() => handleSort("where_name")}>จุดวาง {getSortIcon("where_name")}</th>
                            <th className="sortable col-date-placed" onClick={() => handleSort("when_placed")}>วันที่วาง {getSortIcon("when_placed")}</th>
                            <th className="sortable col-date-end" onClick={() => handleSort("when_end")}>ครบกำหนด {getSortIcon("when_end")}</th>
                            <th className="sortable col-status" onClick={() => handleSort("status")}>สถานะ {getSortIcon("status")}</th>
                            <th className="sortable col-team" onClick={() => handleSort("team_name")}>ทีมติดตั้ง {getSortIcon("team_name")}</th>
                            <th className="sortable col-team" onClick={() => handleSort("team_name")}>ทีมเก็บ {getSortIcon("team_name")}</th>
                            <th className="sticky-action">จัดการ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedGroups.length > 0 ? (
                            paginatedGroups.map((group, gi) => {
                              const first = group.items[0];
                              // unique locations (preserve order)
                              const seenLoc = new Set();
                              const locations = group.items.filter((r) => {
                                const k = `${String(r.where_name || "").trim()}__${String(r.where_full || "").trim()}`;
                                if (seenLoc.has(k)) return false;
                                seenLoc.add(k);
                                return true;
                              });
                              // earliest placement date
                              const earliestPlaced = group.items.reduce((min, r) => {
                                const d = r.checkout_at || r.when_placed;
                                return !min || (d && d < min) ? d : min;
                              }, null);
                              // latest end date
                              const latestEnd = group.items.reduce((max, r) => {
                                return !max || (r.when_end && r.when_end > max) ? r.when_end : max;
                              }, null);
                              // most active status (non-4 takes priority)
                              const activeRow = group.items.find((r) => r.status !== 4) || first;
                              // unique teams
                              const teams = [...new Set(group.items.map((r) => r.team_name).filter(Boolean))];
                              const totalRemainQty = group.items
                                .filter((r) => !isCollectedRow(r))
                                .reduce(
                                  (sum, r) => sum + getItemSummaryTotalQty(r.item_summary || r.Item_Summary || ""),
                                  0
                                );

                              return (
                                <tr
                                  key={group.key}
                                  className={String(group.key || "") === String(focusedDocKey || "") ? "active-focus-row" : ""}
                                >
                                  <td className="col-seq text-gray-700">{groupFrom + gi + 1}</td>
                                  <td className="col-contract font-medium text-gray-900">
                                    <ContractNoCell
                                      row={first}
                                      backLink={buildBackLinkForDocRow(group.key || first.contract_no || first.wh_doc_temp_id || "")}
                                      backLabel="รายการอุปกรณ์ชั่วคราว"
                                    />
                                  </td>
                                  <td className="col-qty-sum font-medium text-gray-900">
                                    {Number(totalRemainQty || 0).toLocaleString()}
                                  </td>
                                  <td className="col-place cell-multiline">
                                    {locations.map((r, i) => (
                                      <div key={i} className={i > 0 ? "mt-1 pt-1 border-t border-gray-100" : ""}>
                                        <div style={{ fontWeight: 600, color: "#111827" }}>{getPlacementPrimary(r) || "-"}</div>
                                        {getPlacementSecondary(r) && <div style={{ fontSize: 12, color: "#111827", marginTop: 2 }}>{getPlacementSecondary(r)}</div>}
                                      </div>
                                    ))}
                                  </td>
                                  <td className="col-date-placed">
                                    <div className="font-medium text-gray-900">{fmtDateTime(earliestPlaced)}</div>
                                  </td>
                                  <td className="col-date-end">{fmtDate(latestEnd)}</td>
                                  <td className="col-status">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${activeRow.status === 4 ? "bg-green-100 text-green-800" : activeRow.status === 3 || activeRow.status === 2 ? "bg-amber-100 text-amber-900" : "bg-blue-50 text-blue-800"}`}>
                                      {activeRow.status_label}
                                    </span>
                                  </td>
                                  <td className="col-team text-left">{teams.join(", ") || "-"}</td>
                                  <td className="col-team text-left">{teams.join(", ") || "-"}</td>
                                  <td className="sticky-action">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!first.job_id) return;
                                        const focusKey = String(group.key || first.contract_no || first.wh_doc_temp_id || "");
                                        setFocusedDocKey(focusKey);
                                        setFocusedItemContractKey(null);
                                        const qs = new URLSearchParams(backLinkWithState.split("?")[1] || "");
                                        if (focusKey) qs.set("focusDoc", focusKey);
                                        qs.delete("focusItem");
                                        const backLink = `${location.pathname}?${qs.toString()}`;
                                        navigate(`/jobfile/${first.job_id}`, {
                                          state: { fromTempEquipment: true, backLink },
                                        });
                                      }}
                                      className="btn btn-sm p-1"
                                      style={{ background: "none", border: "none", cursor: first.job_id ? "pointer" : "not-allowed", opacity: first.job_id ? 1 : 0.4, color: "#E13F57", display: "inline-flex", alignItems: "center" }}
                                      disabled={!first.job_id}
                                      title="เปิดแฟ้มงาน"
                                    >
                                      <Icon icon="mdi:folder-open-outline" width={18} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={10} className="text-center py-4 text-gray-500">
                                {search.trim() ? "ไม่พบรายการที่ค้นหา" : "ไม่มีรายการอุปกรณ์ชั่วคราว"}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === "docs" && (
                  <div className="data-table-footer" style={{ flexShrink: 0 }}>
                    <Pagination
                      totalItems={totalGroups}
                      itemsPerPage={pageSize}
                      currentPage={safeCurrentPage}
                      compact
                      onPageChange={(p) =>
                        setCurrentPage(Number.isFinite(Number(p)) ? Number(p) : 1)
                      }
                      onItemsPerPageChange={(val) => {
                        setItemsPerPage(Number(val));
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                )}

                {/* Tab: รายการอุปกรณ์ */}
                {activeTab === "items" && (
                  <div style={{ flex: 1, overflow: "hidden", padding: "12px 16px", display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <div style={{ flex: 1, minHeight: 0, display: "flex", gap: 16 }}>
                      {/* Left: ประเภทอุปกรณ์ */}
                      <div style={{ width: "38%", flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <DataTable
                          columns={[
                            { title: "#", width: 50, align: "center", render: (_, __, i) => i + 1 },
                            {
                              title: "ชื่ออุปกรณ์",
                              dataIndex: "name",
                              render: (v) => <span style={{ fontSize: 13 }}>{v}</span>,
                            },
                            {
                              title: "วาง",
                              dataIndex: "count",
                              width: 70,
                              align: "center",
                              render: (v) => {
                                const n = Number(v || 0);
                                return <Tag color={n > 0 ? "blue" : "default"} style={{ fontSize: 12 }}>{n}</Tag>;
                              },
                            },
                            {
                              title: "หน่วย",
                              dataIndex: "unit",
                              width: 70,
                              align: "center",
                              render: (v) => <span style={{ fontSize: 12, color: "#111827" }}>{v || "-"}</span>,
                            },
                          ]}
                          dataSource={itemStats}
                          loading={loading}
                          rowKey="name"
                          defaultPageSize={100}
                          pageSizeOptions={[10, 20, 50, 100]}
                          alwaysScroll
                          onRow={(record) => ({ onClick: () => setSelectedItemName(record.name) })}
                          rowClassName={(record) => record.name === selectedItemName ? "active-focus-row" : ""}
                          components={{ header: { cell: (props) => <th {...props} style={{ ...props.style, backgroundColor: "#E13F57", color: "#fff", fontWeight: 600 }} /> } }}
                        />
                      </div>

                      {/* Right: เอกสารของอุปกรณ์ที่เลือก */}
                      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden", background: "#fff", minHeight: 0 }}>
                          {selectedItemName ? (
                            <>
                              <div style={{ flex: 1, minHeight: 0 }}>
                                <DataTable
                                  rowKey={(r, i) => r.wh_doc_temp_id ?? i}
                                  dataSource={itemFilteredRows}
                                  defaultPageSize={100}
                                  pageSizeOptions={[10, 20, 50, 100]}
                                  alwaysScroll
                                  components={{ header: { cell: (props) => <th {...props} style={{ ...props.style, backgroundColor: "#E13F57", color: "#fff", fontWeight: 600 }} /> } }}
                                  rowClassName={(r) => {
                                    const key = `${String(r.contract_no || "-")}__${String(getCustomerName(r) || "-")}`;
                                    return key === focusedItemContractKey ? "active-focus-row" : "";
                                  }}
                                  columns={[
                                    { title: "#", width: 50, align: "center", render: (_, __, i) => i + 1 },
                                    {
                                      title: "เลขที่สัญญา",
                                      width: 140,
                                      render: (_, r) => (
                                        <ContractNoCell
                                          row={r}
                                          backLink={buildBackLinkForItemRow(r)}
                                          backLabel="รายการอุปกรณ์ชั่วคราว"
                                        />
                                      ),
                                    },
                                    {
                                      title: "จำนวนสินค้า",
                                      width: 120,
                                      align: "center",
                                      render: (_, r) => {
                                        const qty = Number(r.item_qty_in_contract || 0).toLocaleString();
                                        return (
                                          <span style={{ fontSize: 13, fontWeight: 600 }}>
                                            {selectedItemUnit ? `${qty} ${selectedItemUnit}` : qty}
                                          </span>
                                        );
                                      },
                                    },
                                    {
                                      title: "จุดวาง",
                                      render: (_, r) => (
                                        <div>
                                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{getPlacementPrimary(r) || "-"}</div>
                                          {getPlacementSecondary(r) && (
                                            <div style={{ fontSize: 12, color: "#111827", marginTop: 2 }}>{getPlacementSecondary(r)}</div>
                                          )}
                                        </div>
                                      ),
                                    },
                                    {
                                      title: "วันที่วาง",
                                      width: 140,
                                      align: "center",
                                      render: (_, r) => <span style={{ fontSize: 13 }}>{fmtDateTime(r.checkout_at || r.when_placed)}</span>,
                                    },
                                    {
                                      title: "ครบกำหนด",
                                      width: 110,
                                      align: "center",
                                      render: (_, r) => <span style={{ fontSize: 13 }}>{fmtDate(r.when_end)}</span>,
                                    },
                                    {
                                      title: "",
                                      width: 50,
                                      align: "center",
                                      render: (_, r) => (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (!r.job_id) return;
                                            const focusKey = `${String(r.contract_no || "-")}__${String(getCustomerName(r) || "-")}`;
                                            setFocusedItemContractKey(focusKey);
                                            setFocusedDocKey(null);
                                            const qs = new URLSearchParams(backLinkWithState.split("?")[1] || "");
                                            qs.set("tab", "items");
                                            if (selectedItemName) qs.set("item", selectedItemName);
                                            qs.set("focusItem", focusKey);
                                            qs.delete("focusDoc");
                                            const backLink = `${location.pathname}?${qs.toString()}`;
                                            navigate(`/jobfile/${r.job_id}`, {
                                              state: { fromTempEquipment: true, backLink },
                                            });
                                          }}
                                          style={{ background: "none", border: "none", cursor: r.job_id ? "pointer" : "not-allowed", opacity: r.job_id ? 1 : 0.4, color: "#E13F57", display: "inline-flex", alignItems: "center", padding: 4 }}
                                          disabled={!r.job_id}
                                        >
                                          <Icon icon="mdi:folder-open-outline" width={18} />
                                        </button>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
